import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import axios from 'helpers/api';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';

const formatDate = date =>
  date ? moment(date, ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601]).format('MM/DD/YY') : '';

const getGenderName = gender => {
  if (gender === 1 || gender === '1') return 'Male';
  if (gender === 2 || gender === '2') return 'Female';
  return 'Other';
};

const getArrayFromResponse = (data, keys) => {
  const candidates = keys.map(key => data?.[key]).concat(
    keys.map(key => data?.data?.[key]),
    data?.data,
    data
  );

  return candidates.find(Array.isArray) || [];
};

const SelectionModal = ({
  show,
  setShow,
  title,
  label,
  options,
  selectedId,
  setSelectedId,
  loading,
  saving,
  onSubmit,
  // onSearch
}) => (
  <Modal show={show} onHide={() => setShow(false)}>
    <Modal.Header closeButton>
      <Modal.Title as="h5">{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {label != 'User' ? <Form.Group>
        <Form.Label>{label}</Form.Label>
        <ThemedSelect
          options={options}
          value={selectedId}
          onChange={setSelectedId}
          disabled={loading}
        />
      </Form.Group> :
        <Form.Group>
          <Form.Label>{label}</Form.Label>
          {/* <Form.Select
            value={selectedId}
            onChange={event => setSelectedId(event.target.value)}
            disabled={loading}
          >
            <option value="">Select</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select> */}
          <ThemedSelect
            options={options}
            value={selectedId}
            onChange={setSelectedId}
            disabled={loading}
          />
        </Form.Group>}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={() => setShow(false)}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onSubmit} disabled={!selectedId || saving}>
        {saving && <Spinner animation="border" size="sm" className="me-2" />}
        Save
      </Button>
    </Modal.Footer>
  </Modal>
);

const DayProgramDetail = () => {
  const { id } = useParams();
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [showEnrollPatient, setShowEnrollPatient] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [savingUser, setSavingUser] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/users/${id}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      // (data, ['users'])
      const results = data?.data?.map(user => {
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';

        return {
          id: user.user_id, // ✅ FIX
          name: `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`,
          email: user.email || '',
          role: user.role_id || user.role || `Role ${user.role_id}`, // fallback
          assignedDate: formatDate(user.assigned_at) // ✅ FIX
        };
      })
        .sort((a, b) => a.name.localeCompare(b.name));

      setUsers(results);
    } catch (error) {
      setUsers([]);
      toast.error('Unable to load day program users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPatients = async () => {
    setLoadingPatients(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/patients/${id}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      const results = data?.data?.map(patient => {
        return {
          id: patient.patient_id, // ✅ FIX
          name: patient.name || '',
          dob: formatDate(patient.dob),
          gender: getGenderName(patient.gender),
          facility: patient.facility_name || '',
          enrolledDate: formatDate(patient.enrolled_at) // ✅ FIX
        };
      })
        .sort((a, b) => a.name.localeCompare(b.name));

      setPatients(results);
    } catch (error) {
      setPatients([]);
      toast.error('Unable to load day program patients');
    } finally {
      setLoadingPatients(false);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/list`, {},
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      const filteredUsers = data?.filter(user => Number(user.role_id) > 4);
      setUserOptions(
        filteredUsers?.map(user => ({
          value: user.id || user.user_id || user.userId,
          label:
            user.name ||
            `${user.first_name || ''}${user.first_name && user.last_name ? ' ' : ''}${user.last_name || ''}` ||
            user.email,
            role: user.role_id
        }))
      );
    } catch (error) {
      setUserOptions([]);
      toast.error('Unable to load users for assignment');
    }
  };

 
  const fetchAssignablePatients = async () => {
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/patients/list-all-patients/`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      setPatientOptions(
        data?.patients?.data?.map(patient => ({
          value: patient.id,
          label:
            patient.name ||
            patient.patient_name ||
            `${patient.last_name || ''}${patient.last_name && patient.first_name ? ', ' : ''}${patient.first_name || ''}`
        }))
      );
    } catch (error) {
      setPatientOptions([]);
      toast.error('Unable to load patients for enrollment');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPatients();
  }, [id]);

  const handleOpenAssignUser = () => {
    setSelectedUserId('');
    setShowAssignUser(true);
    fetchAssignableUsers();
  };

  const handleOpenEnrollPatient = () => {
    setSelectedPatientId('');
    setShowEnrollPatient(true);
    fetchAssignablePatients();
  };

  const handleAssignUser = async () => {
    setSavingUser(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/assign-user`,
        { day_program_id: id, user_id: selectedUserId?.value },
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success('User assigned successfully');
      setShowAssignUser(false);
      fetchUsers();
    } catch (error) {
      toast.error('Unable to assign user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleEnrollPatient = async () => {
    setSavingPatient(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/enroll-patient`,
        { day_program_id: id, patient_id: selectedPatientId?.value },
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success('Patient enrolled successfully');
      setShowEnrollPatient(false);
      fetchPatients();
    } catch (error) {
      toast.error('Unable to enroll patient');
    } finally {
      setSavingPatient(false);
    }
  };

  const handleRemoveUser = async userId => {
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/remove-user`,
        { day_program_id: id, user_id: userId },
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success('User removed successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Unable to remove user');
    }
  };

  const handleUnenrollPatient = async patientId => {
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/unenroll-patient`,
        { day_program_id: id, patient_id: patientId },
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success('Patient unenrolled successfully');
      fetchPatients();
    } catch (error) {
      toast.error('Unable to unenroll patient');
    }
  };

  const userColumns = [
    { accessorKey: 'name', header: 'Name', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'email', header: 'Email', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'role', header: 'Role', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'assignedDate', header: 'Assigned Date', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    // {
    //   accessorKey: 'actions',
    //   header: '',
    //   enableSorting: false,
    //   cell: ({ row: { original } }) => (
    //     <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => handleRemoveUser(original.id)}>
    //       Remove
    //     </Button>
    //   )
    // }
  ];

  const patientColumns = [
    { accessorKey: 'name', header: 'Name', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'dob', header: 'DOB', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'gender', header: 'Gender', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'facility', header: 'Facility', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'enrolledDate', header: 'Enrolled Date', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    // {
    //   accessorKey: 'actions',
    //   header: '',
    //   enableSorting: false,
    //   cell: ({ row: { original } }) => (
    //     <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => handleUnenrollPatient(original.id)}>
    //       Unenroll
    //     </Button>
    //   )
    // }
  ];

  const usersTable = useAdvanceTable({
    data: users,
    columns: userColumns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const patientsTable = useAdvanceTable({
    data: patients,
    columns: patientColumns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Day Programs', href: paths.adminDayPrograms, isActive: false },
    { name: 'Details', href: paths.adminDayProgramDetails.replace(':id', id), isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card className="mt-3">
        <Tab.Container defaultActiveKey="users">
          <Card.Header className="border-bottom">
            <Nav variant="tabs" className="border-0">
              <Nav.Item>
                <Nav.Link eventKey="users">Users</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="patients">Patients</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>
              <Tab.Pane eventKey="users">
                <AdvanceTableProvider {...usersTable}>
                  <Row className="align-items-center mb-3 g-2">
                    <Col xs="12" md="4">
                      <AdvanceTableSearchBox placeholder="Search..." />
                    </Col>
                    <Col xs="auto" className="ms-auto">
                      <Button variant="primary" size="sm" onClick={handleOpenAssignUser}>
                        <FontAwesomeIcon icon="plus" className="me-2" />
                        Assign User
                      </Button>
                    </Col>
                  </Row>
                  {loadingUsers ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                  ) : users.length > 0 ? (
                    <>
                      <AdvanceTable headerClassName="bg-200 text-nowrap align-middle text-center" rowClassName="text-nowrap align-middle" tableProps={{ size: 'sm', striped: true, className: 'fs-10 mb-0 overflow-hidden' }} />
                      <div className="mt-3"><AdvanceTableFooter rowInfo navButtons rowsPerPageSelection /></div>
                    </>
                  ) : (
                    <div className="text-center text-danger fw-bold py-4">No users assigned</div>
                  )}
                </AdvanceTableProvider>
              </Tab.Pane>
              <Tab.Pane eventKey="patients">
                <AdvanceTableProvider {...patientsTable}>
                  <Row className="align-items-center mb-3 g-2">
                    <Col xs="12" md="4">
                      <AdvanceTableSearchBox placeholder="Search..." />
                    </Col>
                    <Col xs="auto" className="ms-auto">
                      <Button variant="primary" size="sm" onClick={handleOpenEnrollPatient}>
                        <FontAwesomeIcon icon="plus" className="me-2" />
                        Enroll Patient
                      </Button>
                    </Col>
                  </Row>
                  {loadingPatients ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                  ) : patients.length > 0 ? (
                    <>
                      <AdvanceTable headerClassName="bg-200 text-nowrap align-middle text-center" rowClassName="text-nowrap align-middle" tableProps={{ size: 'sm', striped: true, className: 'fs-10 mb-0 overflow-hidden' }} />
                      <div className="mt-3"><AdvanceTableFooter rowInfo navButtons rowsPerPageSelection /></div>
                    </>
                  ) : (
                    <div className="text-center text-danger fw-bold py-4">No patients enrolled</div>
                  )}
                </AdvanceTableProvider>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Tab.Container>
      </Card>

      <SelectionModal
        show={showAssignUser}
        setShow={setShowAssignUser}
        title="Assign User"
        label="User"
        options={userOptions}
        selectedId={selectedUserId}
        setSelectedId={setSelectedUserId}
        loading={false}
        saving={savingUser}
        onSubmit={handleAssignUser}
        // onSearch={handleUserSearch}
      />
      <SelectionModal
        show={showEnrollPatient}
        setShow={setShowEnrollPatient}
        title="Enroll Patient"
        label="Patient"
        options={patientOptions}
        selectedId={selectedPatientId}
        setSelectedId={setSelectedPatientId}
        loading={false}
        saving={savingPatient}
        onSubmit={handleEnrollPatient}
        // onSearch={handlePatientSearch}
      />
    </>
  );
};

export default DayProgramDetail;
